import { Box, Button, Stack, TextField, Typography } from '@mui/material';

import { MultiLangInput } from '@starjack/shared-components';
import { FunctionComponent, useEffect, useState } from 'react';
import { CreateFaqGroup, FaqGroup, isOfTypeFaqGroup } from '../model/faq.types';
import { LoadingButton } from '@mui/lab';
import { useNotification } from '@starjack/features/notifications';
import {
    usePostFaqGroupMutation,
    usePutFaqGroupMutation,
} from '@starjack/entities/faq';

type GroupDrawerContentProps = {
    onClose: () => void;
    group: FaqGroup | CreateFaqGroup;
};

export const GroupDrawerContent: FunctionComponent<GroupDrawerContentProps> = ({
    group,
    onClose,
}) => {
    const [localGroup, setLocalGroup] = useState<FaqGroup | CreateFaqGroup>(
        group
    );
    const { addNotification } = useNotification();

    const [postFaqGroup, { isLoading: postLoading }] =
        usePostFaqGroupMutation();
    const [putFaqGroup, { isLoading: putLoading }] = usePutFaqGroupMutation();

    const onSaveClick = () => {
        const mutationHandler = isOfTypeFaqGroup(localGroup)
            ? putFaqGroup
            : postFaqGroup;

        mutationHandler(localGroup)
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Speichern erfolgreich',
                    type: 'success',
                });

                onClose();
            })
            .catch(() => {
                addNotification({
                    message:
                        'Fehler beim Speichern. Bitte später noch einmal versuchen',
                    type: 'error',
                });
            });
    };

    useEffect(() => {
        setLocalGroup(group);
    }, [group]);

    return (
        <Box
            sx={{
                width: '400px',
            }}
        >
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.dark,
                    color: '#fff',
                    p: 4,
                })}
            >
                <Typography variant='h6'>
                    {isOfTypeFaqGroup(localGroup)
                        ? 'Themenbereich bearbeiten'
                        : 'Neuen Themenbereich erstellen'}
                </Typography>
            </Box>

            <Stack
                sx={{
                    p: 4,
                }}
                spacing={3}
            >
                <MultiLangInput
                    sx={{
                        width: '100%',
                    }}
                    label='Name zur Ausgabe im Webshop'
                    value={localGroup.name}
                    onChange={(name) => {
                        setLocalGroup((prevState) => ({
                            ...prevState,
                            name,
                        }));
                    }}
                />

                <TextField
                    type='number'
                    label='Priorität'
                    value={localGroup.priority}
                    onChange={(e) => {
                        const { value } = e.target;

                        const valueParsed = parseInt(value);
                        setLocalGroup((prevState) => ({
                            ...prevState,
                            priority: valueParsed < 0 ? 0 : valueParsed,
                        }));
                    }}
                    sx={{
                        width: '100%',
                    }}
                />

                <Stack
                    spacing={2}
                    sx={{
                        width: '100%',
                        alignItems: 'center',
                    }}
                >
                    <LoadingButton
                        onClick={onSaveClick}
                        sx={{
                            width: '100%',
                        }}
                        variant='contained'
                        loading={putLoading || postLoading}
                        disabled={!localGroup.name.de}
                    >
                        Speichern
                    </LoadingButton>
                    <Button
                        onClick={onClose}
                        sx={{
                            width: '200px',
                        }}
                        variant='outlined'
                    >
                        Abbrechen
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
};
